/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { signOut } from "firebase/auth";
import {
  ShieldCheck,
  LogOut,
  LockKeyhole,
  QrCode,
  Copy,
  Download,
  TriangleAlert,
} from "lucide-react";

import { auth, functions } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";

export default function SetupMFA() {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();

  const inputRef = useRef(null);

  const [manualCode, setManualCode] = useState("");
  const [qrCode, setQrCode] = useState("");
  const [token, setToken] = useState("");

  const [loadingSetup, setLoadingSetup] = useState(true);
  const [loading, setLoading] = useState(false);

  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [showRecoveryScreen, setShowRecoveryScreen] = useState(false);

  const tokenArray = useMemo(
    () => Array.from({ length: 6 }).map((_, i) => token[i] || ""),
    [token]
  );

  useEffect(() => {
    let alive = true;

    async function beginSetup() {
      try {
        setLoadingSetup(true);
        setError("");

        const beginMfaSetup = httpsCallable(
          functions,
          "beginMfaSetup"
        );

        const res = await beginMfaSetup();

        if (!alive) return;

        setQrCode(res.data?.qrCode || "");
        setManualCode(res.data?.manualCode || "");
      } catch (err) {
        console.error(err);

        if (alive) {
          setError("Failed to start MFA setup.");
        }
      } finally {
        if (alive) {
          setLoadingSetup(false);
        }
      }
    }

    beginSetup();

    return () => {
      alive = false;
    };
  }, []);

  async function completeSetup() {
    try {
      setLoading(true);
      setError("");

      const verifyMfaSetup = httpsCallable(
        functions,
        "verifyMfaSetup"
      );

      await verifyMfaSetup({ token });

      const generateRecoveryCodes = httpsCallable(
        functions,
        "generateRecoveryCodes"
      );

      const recoveryRes =
        await generateRecoveryCodes();

setRecoveryCodes(recoveryRes.data?.recoveryCodes || []);
setShowRecoveryScreen(true);
    } catch (err) {
      console.error(err);

      setError(
        "Invalid authentication code. Please use the latest code from your authenticator app."
      );

      setTimeout(() => {
        setToken("");
        inputRef.current?.focus();
      }, 120);
    } finally {
      setLoading(false);
    }
  }

  async function copySecret() {
    try {
      await navigator.clipboard.writeText(manualCode);

      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 1600);
    } catch {
      setCopied(false);
    }
  }

  async function copyRecoveryCodes() {
    try {
      await navigator.clipboard.writeText(
        recoveryCodes.join("\n")
      );

      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 1600);
    } catch {}
  }

  function downloadRecoveryCodes() {
    const blob = new Blob(
      [
        `PresentPlus Recovery Codes\n\n${recoveryCodes.join(
          "\n"
        )}`,
      ],
      { type: "text/plain;charset=utf-8" }
    );

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;
    link.download = "presentplus-recovery-codes.txt";

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }

  async function logoutNow() {
    await signOut(auth);

    navigate("/login", { replace: true });
  }

  if (showRecoveryScreen) {
    return (
      <div style={styles.page}>
        <div style={styles.glowOne} />
        <div style={styles.glowTwo} />

        <div style={styles.card}>
          <div style={styles.hero}>
            <div style={styles.heroCircle}>
              <ShieldCheck size={42} color="#ffffff" />
            </div>

            <div style={styles.ringOne} />
            <div style={styles.ringTwo} />
          </div>

          <div style={styles.eyebrow}>
            <ShieldCheck size={14} />
            RECOVERY CODES READY
          </div>

          <h1 style={styles.title}>
            Save your recovery codes
          </h1>

          <p style={styles.text}>
            These one-time recovery codes can be used if you
            lose access to your authenticator device.
          </p>

          <div style={styles.warningCard}>
            <TriangleAlert size={20} />

            <div>
              <div style={styles.warningTitle}>
                Store these securely
              </div>

              <div style={styles.warningText}>
                Each recovery code works once only.
                Anyone with these codes can access your
                admin account.
              </div>
            </div>
          </div>

          <div style={styles.recoveryGrid}>
            {recoveryCodes.map((code) => (
              <div key={code} style={styles.recoveryCode}>
                {code}
              </div>
            ))}
          </div>

          <div style={styles.recoveryActions}>
            <button
              type="button"
              onClick={copyRecoveryCodes}
              style={styles.copyButton}
            >
              <Copy size={16} />
              {copied ? "Copied" : "Copy codes"}
            </button>

            <button
              type="button"
              onClick={downloadRecoveryCodes}
              style={styles.downloadButton}
            >
              <Download size={16} />
              Download
            </button>
          </div>

<button
  onClick={async () => {
    await refreshProfile?.();
    navigate("/admin-manager", { replace: true });
  }}
  style={styles.primary}
>
  I have saved my recovery codes
</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.glowOne} />
      <div style={styles.glowTwo} />

      <div style={styles.card}>
        <div style={styles.hero}>
          <div style={styles.heroCircle}>
            <ShieldCheck size={42} color="#ffffff" />
          </div>

          <div style={styles.ringOne} />
          <div style={styles.ringTwo} />
        </div>

        <div style={styles.eyebrow}>
          <ShieldCheck size={14} />
          SECURITY SETUP
        </div>

        <h1 style={styles.title}>
          Set up multi-factor authentication
        </h1>

        <p style={styles.text}>
          Scan the QR code with Google Authenticator,
          Microsoft Authenticator, or Authy.
        </p>

        <div style={styles.userCard}>
          <div style={styles.userIcon}>
            <LockKeyhole size={18} />
          </div>

          <div style={{ flex: 1 }}>
            <div style={styles.userName}>
              {profile?.displayName || "Admin user"}
            </div>

            <div style={styles.userEmail}>
              {profile?.email || user?.email}
            </div>
          </div>

          <div style={styles.adminBadge}>
            Admin
          </div>
        </div>

        <div style={styles.qrPanel}>
          <div style={styles.qrHeader}>
            <div>
              <div style={styles.qrEyebrow}>
                STEP 1
              </div>

              <div style={styles.qrTitle}>
                Scan QR code
              </div>
            </div>

            <QrCode size={22} color="#5b4dff" />
          </div>

          {loadingSetup ? (
            <div style={styles.qrLoading}>
              Generating secure QR...
            </div>
          ) : qrCode ? (
            <img
              src={qrCode}
              alt="MFA QR"
              style={styles.qr}
            />
          ) : (
            <div style={styles.qrLoading}>
              QR could not be generated.
            </div>
          )}
        </div>

        <div style={styles.secretBox}>
          <div>
            <span style={styles.secretLabel}>
              Manual setup code
            </span>

            <code style={styles.secretCode}>
              {manualCode || "—"}
            </code>
          </div>

          <button
            type="button"
            onClick={copySecret}
            style={styles.copyButton}
          >
            <Copy size={16} />
            {copied ? "Copied" : "Copy"}
          </button>
        </div>

        <div style={styles.codeLabel}>
          STEP 2 · ENTER 6-DIGIT CODE
        </div>

        <div
          style={styles.codeBoxes}
          onClick={() => inputRef.current?.focus()}
        >
          {tokenArray.map((digit, index) => (
            <div
              key={index}
              style={{
                ...styles.codeBox,
                borderColor:
                  index === token.length
                    ? "#5b4dff"
                    : "#dbe7ff",
              }}
            >
              {digit || "•"}
            </div>
          ))}
        </div>

        <input
          ref={inputRef}
          id="mfa-setup-code"
          name="mfa-setup-code"
          autoComplete="one-time-code"
          value={token}
          onChange={(e) => {
            setToken(
              e.target.value
                .replace(/\D/g, "")
                .slice(0, 6)
            );

            if (error) setError("");
          }}
          inputMode="numeric"
          autoFocus
          maxLength={6}
          style={styles.hiddenInput}
        />

        {error ? (
          <div style={styles.error}>{error}</div>
        ) : null}

        <button
          onClick={completeSetup}
          disabled={
            loading ||
            loadingSetup ||
            token.length !== 6
          }
          style={{
            ...styles.primary,
            opacity:
              loading ||
              loadingSetup ||
              token.length !== 6
                ? 0.6
                : 1,
          }}
        >
          <ShieldCheck size={18} />
          {loading
            ? "Verifying..."
            : "Complete MFA setup"}
        </button>

        <button
          onClick={logoutNow}
          style={styles.secondary}
        >
          <LogOut size={18} />
          Logout
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    background: "linear-gradient(180deg,#eef2ff 0%,#f8fafc 100%)",
    position: "relative",
    overflow: "hidden",
  },
  glowOne: {
    position: "absolute",
    width: 520,
    height: 520,
    borderRadius: "50%",
    background: "radial-gradient(circle,#7c3aed22 0%,transparent 70%)",
    top: -170,
    left: -150,
  },
  glowTwo: {
    position: "absolute",
    width: 440,
    height: 440,
    borderRadius: "50%",
    background: "radial-gradient(circle,#5b4dff15 0%,transparent 70%)",
    bottom: -130,
    right: -120,
  },
  card: {
    width: "100%",
    maxWidth: 580,
    background: "rgba(255,255,255,0.9)",
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    border: "1px solid rgba(219,231,255,0.9)",
    borderRadius: 28,
    padding: 28,
    position: "relative",
    boxShadow: "0 30px 90px rgba(15,23,42,0.10)",
  },
  hero: {
    position: "relative",
    width: 120,
    height: 120,
    margin: "0 auto 42px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  heroCircle: {
    width: 120,
    height: 120,
    borderRadius: "50%",
    background: "linear-gradient(135deg,#5b4dff,#7c3aed)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    inset: 0,
    margin: "auto",
    zIndex: 2,
    boxShadow: "0 25px 50px rgba(91,77,255,0.35)",
  },
  ringOne: {
    position: "absolute",
    inset: -14,
    borderRadius: "50%",
    border: "2px solid rgba(91,77,255,0.12)",
  },
  ringTwo: {
    position: "absolute",
    inset: -28,
    borderRadius: "50%",
    border: "2px solid rgba(91,77,255,0.08)",
  },
  eyebrow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    fontSize: 13,
    fontWeight: 900,
    letterSpacing: "0.16em",
    color: "#5b4dff",
    marginBottom: 18,
  },
  title: {
    fontSize: 34,
    lineHeight: 1,
    textAlign: "center",
    fontWeight: 950,
    color: "#102043",
    margin: 0,
    marginBottom: 20,
  },
  text: {
    textAlign: "center",
    color: "#64748b",
    fontSize: 15,
    lineHeight: 1.6,
    maxWidth: 620,
    margin: "0 auto 30px",
  },
  userCard: {
    display: "flex",
    alignItems: "center",
    gap: 18,
    background: "linear-gradient(180deg,#ffffff,#f8fbff)",
    border: "1px solid #dbe7ff",
    borderRadius: 24,
    padding: 20,
    marginBottom: 22,
  },
  userIcon: {
    width: 52,
    height: 52,
    borderRadius: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg,#eef2ff,#f5f3ff)",
    color: "#5b4dff",
  },
  userName: {
    fontWeight: 900,
    fontSize: 20,
    color: "#102043",
    marginBottom: 4,
  },
  userEmail: {
    color: "#64748b",
    fontSize: 15,
  },
  adminBadge: {
    background: "linear-gradient(135deg,#ede9fe,#f5f3ff)",
    color: "#6d28d9",
    fontWeight: 900,
    padding: "10px 16px",
    borderRadius: 999,
    fontSize: 13,
  },
  qrPanel: {
    borderRadius: 26,
    border: "1px solid #dbe7ff",
    background: "linear-gradient(180deg,#ffffff,#f8fbff)",
    padding: 22,
    marginBottom: 18,
  },
  qrHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 18,
  },
  qrEyebrow: {
    fontSize: 11,
    fontWeight: 900,
    color: "#5b4dff",
    letterSpacing: "0.14em",
  },
  qrTitle: {
    fontSize: 20,
    fontWeight: 950,
    color: "#102043",
    marginTop: 4,
  },
  qr: {
    width: 170,
    height: 170,
    display: "block",
    margin: "0 auto",
    borderRadius: 24,
    border: "1px solid #e2e8f0",
    padding: 12,
    background: "#ffffff",
  },
  qrLoading: {
    height: 230,
    display: "grid",
    placeItems: "center",
    color: "#64748b",
    fontWeight: 800,
  },
  secretBox: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "center",
    padding: 18,
    borderRadius: 22,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    marginBottom: 24,
  },
  secretLabel: {
    display: "block",
    fontSize: 13,
    fontWeight: 900,
    color: "#64748b",
    marginBottom: 7,
  },
  secretCode: {
    color: "#102043",
    fontWeight: 900,
    wordBreak: "break-all",
    fontSize: 14,
  },
  copyButton: {
    border: "1px solid #dbe7ff",
    background: "#ffffff",
    color: "#5b4dff",
    borderRadius: 16,
    padding: "12px 14px",
    fontWeight: 900,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 7,
  },
  codeLabel: {
    fontSize: 13,
    fontWeight: 900,
    letterSpacing: "0.16em",
    color: "#64748b",
    marginBottom: 16,
  },
  codeBoxes: {
    display: "grid",
    gridTemplateColumns: "repeat(6,1fr)",
    gap: 14,
    marginBottom: 24,
    cursor: "text",
  },
  codeBox: {
    height: 56,
    borderRadius: 14,
    border: "2px solid #dbe7ff",
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 24,
    fontWeight: 900,
    color: "#102043",
    transition: "all .2s ease",
  },
  hiddenInput: {
    position: "absolute",
    width: 1,
    height: 1,
    opacity: 0,
    left: -9999,
  },
  error: {
    background: "#fef2f2",
    color: "#dc2626",
    padding: 16,
    borderRadius: 18,
    marginBottom: 18,
    fontWeight: 800,
    border: "1px solid #fecaca",
  },
  primary: {
    width: "100%",
    height: 64,
    border: 0,
    borderRadius: 22,
    background: "linear-gradient(135deg,#5b4dff,#7c3aed)",
    color: "#fff",
    fontWeight: 900,
    fontSize: 18,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    boxShadow: "0 18px 40px rgba(91,77,255,0.30)",
  },
  secondary: {
    width: "100%",
    height: 60,
    borderRadius: 22,
    border: "1px solid #dbe7ff",
    background: "#fff",
    color: "#102043",
    fontWeight: 900,
    fontSize: 17,
    cursor: "pointer",
    marginTop: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },
  footer: {
    textAlign: "center",
    marginTop: 26,
    color: "#64748b",
    fontSize: 14,
    fontWeight: 700,
  },
  warningCard: {
  display: "flex",
  gap: 16,
  alignItems: "flex-start",
  padding: 22,
  borderRadius: 24,
  background: "#fff7ed",
  border: "1px solid #fed7aa",
  marginBottom: 28,
  color: "#9a3412",
},

warningTitle: {
  fontSize: 18,
  fontWeight: 900,
  marginBottom: 6,
},

warningText: {
  lineHeight: 1.6,
  fontSize: 15,
},

recoveryGrid: {
  display: "grid",
  gridTemplateColumns: "repeat(2,1fr)",
  gap: 14,
  marginBottom: 28,
},

recoveryCode: {
  background: "#ffffff",
  border: "1px solid #dbe7ff",
  borderRadius: 18,
  padding: 18,
  fontWeight: 900,
  fontSize: 16,
  textAlign: "center",
  color: "#102043",
},

recoveryActions: {
  display: "flex",
  gap: 14,
  marginBottom: 24,
},

downloadButton: {
  flex: 1,
  height: 56,
  borderRadius: 18,
  border: "1px solid #dbe7ff",
  background: "#ffffff",
  color: "#102043",
  fontWeight: 900,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
},
};
